import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './FileSelectWithPreview.css';

export default function FileSelectWithPreview({
  items, value, onChange, renderLabel, previewUrl,
  placeholder = 'Select a file…', emptyLabel = 'No files found',
}) {
  const [open, setOpen] = useState(false);
  const [preview,        setPreview]        = useState(null);   // {columns, rows} | null
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError,   setPreviewError]   = useState(null);
  const [previewOpen,    setPreviewOpen]    = useState(true);
  const ref = useRef();

  const selectedItem = items.find(i => i.name === value) || null;

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Load a preview whenever the selection changes (covers both clicking an
  // item and a default/auto-selected value).
  useEffect(() => {
    if (!selectedItem) { setPreview(null); setPreviewError(null); return; }
    setPreviewLoading(true);
    setPreviewError(null);
    axios.get(previewUrl(selectedItem))
      .then(r => setPreview(r.data))
      .catch(e => setPreviewError(e.response?.data?.error || e.message))
      .finally(() => setPreviewLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="fsp-wrap" ref={ref}>
      <div className={`fsp-trigger ${open ? 'open' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className={selectedItem ? '' : 'fsp-placeholder'}>
          {selectedItem ? renderLabel(selectedItem) : placeholder}
        </span>
        <span className="fsp-chevron">▾</span>
      </div>
      {open && (
        <div className="fsp-dropdown">
          {items.length === 0
            ? <div className="fsp-empty">{emptyLabel}</div>
            : items.map(item => (
              <div
                key={item.name}
                className={`fsp-item ${value === item.name ? 'selected' : ''}`}
                onClick={() => { onChange(item.name); setOpen(false); }}
              >
                {renderLabel(item)}
              </div>
            ))
          }
        </div>
      )}

      {selectedItem && (
        <div className="fsp-preview">
          <div className="fsp-preview-header" onClick={() => setPreviewOpen(o => !o)}>
            <span className={`fsp-preview-chevron ${previewOpen ? 'open' : ''}`}>▸</span>
            Preview
            {previewLoading && <span className="fsp-preview-loading">Loading…</span>}
          </div>
          {previewOpen && (
            <>
              {previewError && <div className="fsp-preview-error">{previewError}</div>}
              {preview && (
                preview.rows.length === 0 ? (
                  <div className="fsp-preview-empty">(empty file)</div>
                ) : (
                  <div className="fsp-preview-table-wrap">
                    <table className="fsp-preview-table">
                      <thead>
                        <tr>{preview.columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((row, i) => (
                          <tr key={i}>{row.map((cell, j) => <td key={j}>{String(cell)}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
